import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import AIResponse from "./faq/AIResponse";

const AIAssistant = () => {
  const [question, setQuestion] = useState("");
  const [aiResponse, setAiResponse] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!question.trim()) return;

    const currentQuestion = question.trim();
    setQuestion("");
    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('ai-chat', {
        body: { question: currentQuestion }
      });

      if (error) {
        throw error;
      }

      if (!data.success) {
        throw new Error(data.error || 'Erreur lors de la génération de la réponse');
      }

      setAiResponse(data.response);
    } catch (error) {
      console.error('Error calling AI:', error);
      toast({
        title: "Erreur",
        description: "Désolée, je ne peux pas répondre pour le moment. N'hésitez pas à me contacter directement !",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const welcomeMessage = "Bien que je préfère toujours échanger directement avec vous, mon double numérique est à votre disposition 24h/24.";

  return (
    <div className="bg-bbz-cream py-8 md:py-16 px-0 md:px-8">
      <div className="container mx-auto max-w-3xl px-2 md:px-0">
        {/* Welcome Message */}
        <div className="mb-8 md:mb-12 p-2 md:p-8 bg-gradient-to-r from-bbz-cream/30 to-bbz-green/10 rounded-lg border border-bbz-green/20">
          <div className="flex items-start gap-4">
            <Avatar className="flex-shrink-0 w-20 h-20">
              <AvatarImage 
                src="/images/portrait-valerie.jpg" 
                alt="Valérie - Esthéticienne" 
                className="object-cover object-[center_-15%] scale-125" 
              />
              <AvatarFallback className="bg-bbz-green text-white">
                <Sparkles className="w-6 h-6" />
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="text-xl md:text-2xl font-semibold text-bbz-text mb-3">Une question sur l'institut ? Posez la à Valér-IA</h3>
              <p className="text-bbz-text/80 text-base leading-relaxed">
                {welcomeMessage}
              </p>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <form onSubmit={handleSubmit} className="mb-6">
          <div className="relative">
            <Input 
              type="text" 
              placeholder="Posez votre question à Valér-IA..." 
              value={question} 
              onChange={(e) => setQuestion(e.target.value)} 
              className="pr-16 py-4 text-lg h-14 border-bbz-green/50 focus:border-bbz-green focus:ring-bbz-green" 
              disabled={isLoading} 
            />
            <Button 
              type="submit" 
              size="sm" 
              disabled={!question.trim() || isLoading} 
              className={`absolute right-2 top-2 h-10 w-10 p-0 transition-all duration-300 ${
                question.trim() 
                  ? 'bg-green-700 hover:bg-green-800 animate-pulse scale-110 shadow-lg shadow-green-700/40' 
                  : 'bg-bbz-green/50 hover:bg-bbz-green/70'
              }`}
            >
              <Send className={`w-5 h-5 transition-transform duration-300 ${
                question.trim() ? 'scale-110 text-white' : ''
              }`} />
            </Button>
          </div>
        </form>

        {/* AI Response */}
        {(aiResponse || isLoading) && (
          <div className="mb-8">
            <AIResponse content={aiResponse} isLoading={isLoading} />
          </div>
        )}

        {/* Quick Questions Suggestions */}
        {!aiResponse && !isLoading && (
          <div className="space-y-4">
            <p className="text-base font-medium text-bbz-text/70 mb-4">Questions fréquentes :</p>
            <div className="grid gap-3 md:grid-cols-2">
              {["Quels types de soins proposez-vous ?", "Comment prendre rendez-vous ?", "Quels sont vos horaires ?", "Utilisez-vous des produits bio ?"].map((suggestion) => (
                <Button 
                  key={suggestion} 
                  variant="outline" 
                  size="default" 
                  onClick={() => setQuestion(suggestion)} 
                  className="text-left justify-start h-auto py-3 px-4 text-base text-bbz-text border-bbz-green/30 hover:border-bbz-green hover:bg-bbz-cream/50"
                >
                  {suggestion}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIAssistant;